import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Text,
  Select,
  Alert,
  Flex,
} from '@backstage/ui';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { CodeSnippet } from '@backstage/core-components';

interface ApiEndpoint {
  name: string;
  method: 'GET' | 'POST' | 'DELETE';
  path: string;
  requiresProvider?: boolean;
  description: string;
}

interface ListProvidersResponse {
  success: boolean;
  providers: string[];
}

const LIST_PROVIDERS_PATH = '/api/catalog/incremental/providers';

const API_ENDPOINTS: ApiEndpoint[] = [
  {
    name: 'List Providers',
    method: 'GET',
    path: LIST_PROVIDERS_PATH,
    description: 'Get all incremental entity providers',
  },
  {
    name: 'Health Check',
    method: 'GET',
    path: '/api/catalog/incremental/health',
    description: 'Check health of all providers',
  },
  {
    name: 'Provider Status',
    method: 'GET',
    path: '/api/catalog/incremental/providers/{provider}',
    requiresProvider: true,
    description: 'Get specific provider status',
  },
  {
    name: 'Trigger Provider',
    method: 'POST',
    path: '/api/catalog/incremental/providers/{provider}/trigger',
    requiresProvider: true,
    description: 'Trigger next action for provider',
  },
  {
    name: 'Start Provider',
    method: 'POST',
    path: '/api/catalog/incremental/providers/{provider}/start',
    requiresProvider: true,
    description: 'Start new ingestion cycle',
  },
  {
    name: 'Cancel Provider',
    method: 'POST',
    path: '/api/catalog/incremental/providers/{provider}/cancel',
    requiresProvider: true,
    description: 'Cancel current ingestion cycle',
  },
  {
    name: 'Delete Provider',
    method: 'DELETE',
    path: '/api/catalog/incremental/providers/{provider}',
    requiresProvider: true,
    description: 'Delete provider completely',
  },
  {
    name: 'Clear Marks',
    method: 'DELETE',
    path: '/api/catalog/incremental/providers/{provider}/marks',
    requiresProvider: true,
    description: 'Clear ingestion marks for provider',
  },
];

export const IncrementalProvidersCard = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [providerName, setProviderName] = useState<string>('');
  const [providers, setProviders] = useState<string[] | null>(null);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const backendUrl = configApi.getString('backend.baseUrl');

  const fetchProviders = useCallback(async () => {
    setProvidersLoading(true);
    setProvidersError(null);

    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetch(`${backendUrl}${LIST_PROVIDERS_PATH}`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const json: ListProvidersResponse = await response.json();
      const sorted = [...(json.providers ?? [])].sort();
      setProviders(sorted);

      // If the previously selected provider no longer exists in the list,
      // clear it so the user isn't holding a stale reference.
      if (providerName && !sorted.includes(providerName)) {
        setProviderName('');
      }
    } catch (err) {
      setProvidersError(err instanceof Error ? err.message : 'Unknown error');
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  }, [backendUrl, identityApi, providerName]);

  useEffect(() => {
    fetchProviders();
    // We only want to fetch once on mount; subsequent fetches are user-driven
    // via the "Refresh" button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeRequest = async (endpoint: ApiEndpoint, endpointKey: string) => {
    setLoading(prev => ({ ...prev, [endpointKey]: true }));

    try {
      const { token } = await identityApi.getCredentials();
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      let url = `${backendUrl}${endpoint.path}`;
      if (endpoint.requiresProvider) {
        if (!providerName) {
          throw new Error('Provider name is required for this endpoint');
        }
        url = url.replace('{provider}', providerName);
      }

      const response = await fetch(url, {
        method: endpoint.method,
        headers,
      });

      let responseBody;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseBody = await response.json();
      } else {
        responseBody = await response.text();
      }

      const responseData = {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url,
        headers: Object.fromEntries(response.headers.entries()),
        body: responseBody,
      };

      setResponses(prev => ({ ...prev, [endpointKey]: responseData }));

      // After a destructive or list-changing call, refresh the provider list
      // so the dropdown stays in sync with reality.
      if (
        endpoint.path === LIST_PROVIDERS_PATH ||
        endpoint.method === 'DELETE'
      ) {
        fetchProviders();
      }
    } catch (error) {
      setResponses(prev => ({
        ...prev,
        [endpointKey]: {
          error: error instanceof Error ? error.message : 'Unknown error',
          url: endpoint.requiresProvider ?
            `${backendUrl}${endpoint.path}`.replace('{provider}', providerName || '{provider}') :
            `${backendUrl}${endpoint.path}`,
        },
      }));
    } finally {
      setLoading(prev => ({ ...prev, [endpointKey]: false }));
    }
  };

  const providerOptions = (providers ?? []).map(name => ({
    value: name,
    label: name,
  }));

  return (
    <Card>
      <CardHeader>
        <Flex direction="column" gap="1">
          <Text variant="title-medium">
            Incremental Entity Provider API Management
          </Text>
          <Text variant="body-small" color="secondary">
            Manage the incremental ingestion API endpoints with authentication
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="4">
          <div className="devtools-provider-picker">
            <Text
              variant="title-small"
              className="devtools-provider-picker-eyebrow"
            >
              Active provider
            </Text>
            <Flex align="center" gap="2">
              <div className="devtools-provider-picker-field">
                <Select
                  size="medium"
                  placeholder={
                    providersLoading
                      ? 'Loading providers…'
                      : providerOptions.length === 0
                      ? 'No providers available'
                      : 'Pick a provider — required for provider-specific endpoints'
                  }
                  searchable
                  searchPlaceholder="Search providers…"
                  isDisabled={providersLoading || providerOptions.length === 0}
                  options={providerOptions}
                  selectedKey={providerName || null}
                  onSelectionChange={key => {
                    setProviderName(key == null ? '' : String(key));
                  }}
                />
              </div>
              <Button
                variant="primary"
                size="medium"
                loading={providersLoading}
                onPress={() => {
                  fetchProviders();
                }}
              >
                Refresh
              </Button>
            </Flex>
          </div>

          {providersError && (
            <Alert
              status="danger"
              title="Could not load providers"
              description={providersError}
            />
          )}

          <Flex direction="column" gap="3">
            {API_ENDPOINTS.map(endpoint => {
              const endpointKey = `${endpoint.method}-${endpoint.path}`;
              const isLoading = loading[endpointKey];
              const response = responses[endpointKey];
              const resolvedPath = endpoint.requiresProvider
                ? endpoint.path.replace('{provider}', providerName || '{provider}')
                : endpoint.path;

              return (
                <div key={endpointKey} className="devtools-endpoint">
                  <div className="devtools-endpoint-header">
                    <div className="devtools-endpoint-title">
                      <Text variant="title-small">{endpoint.name}</Text>
                      <Text variant="body-small" color="secondary">
                        {endpoint.description}
                      </Text>
                    </div>
                    <Button
                      variant="primary"
                      size="medium"
                      loading={isLoading}
                      isDisabled={endpoint.requiresProvider && !providerName}
                      onPress={() => {
                        makeRequest(endpoint, endpointKey);
                      }}
                    >
                      Run
                    </Button>
                  </div>

                  <div className="devtools-endpoint-body">
                    <div className="devtools-endpoint-url">
                      <span className="devtools-endpoint-method">
                        {endpoint.method}
                      </span>
                      {resolvedPath}
                    </div>

                    {response && (
                      <Flex direction="column" gap="1">
                        <Text variant="body-small" weight="bold">
                          Response:
                        </Text>
                        <CodeSnippet
                          text={JSON.stringify(response, null, 2)}
                          language="json"
                          showCopyCodeButton
                        />
                      </Flex>
                    )}
                  </div>
                </div>
              );
            })}
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};
